import { DISCOUNT_TYPES } from '@weft/types';
import type { Currency, TierDiscountConfiguration } from '@weft/types';
import { isNotNullOrUndefined, isNullOrUndefined, purge, raiseUnreachable } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import { percentageRatio } from '@weft/utils';
import type { ColumnDefinition } from 'apps/app/src/components/design/table/table-components/table-body/table-body-components/table-row/table-row-components/table-cell/table-cell-types/column-definition';
import classNames from 'classnames';
import { Price } from '../../../components/design/Price';
import { TableToCell } from '../../../components/design/table/table-components/table-body/table-body-components/table-row/table-row-components/table-cell/TableToCell';
import { DiscountCell } from './dynamic-invoice-view-components/UsageLineItemBody/DiscountCell';
import type { DynamicInvoiceTiersTableRow } from './dynamic-invoice-view-page-types/dynamic-invoice-tiers-table-row';

export function buildInvoiceTiersTableColumns(
  lineItemId: string,
  productVariantId: string,
  currency: Currency,
  isEditDisabled: boolean,
  isFlatRate: boolean,
  onResetClick?: (lineItemId: string, productVariantId: string, tierIndex: number) => void,
  isTieredSubscription = false,
): ColumnDefinition<DynamicInvoiceTiersTableRow>[] {
  return purge([    
    {
      headerClassName: 'text-left w-fit',
      header: 'From',      
      renderCell: ({ from }) => (
        <p className='text-left'>
          {formatNumber(from)}
        </p>          
      ),
    },
    { 
      headerClassName: 'text-right',
      header: 'To',
      renderCell: ({ to }) => (
        <TableToCell
          className="top-1.5 w-full text-right"
          to={to}
        />
      ),
    },
    !isTieredSubscription ? {
      header: 'Per Unit',
      headerClassName: 'w-fit m-auto',
      renderCell: ({ price }) => (
        <div className='m-auto w-fit'>
          <Price currency={currency}>
            {price}
          </Price>
        </div>
      ),
    } : undefined,
    !isTieredSubscription ? {
      header: 'QTY',
      headerClassName: 'w-fit m-auto',
      renderCell: ({ tierUnits }) => (
        <p className='m-auto w-fit '>
          {formatNumber(tierUnits)}
        </p>
      ),
    } : undefined,
    isFlatRate ? {
      header: isTieredSubscription ? 'Fixed Rate' : 'Flat Rate',
      headerClassName: 'w-fit m-auto',
      renderCell: ({ flatRate }) => (
        <div className='m-auto w-fit'>
          <Price currency={currency}>
            {flatRate}
          </Price>
        </div>
      ),
    } : undefined,
    {
      header: 'New price',
      headerClassName: 'w-[160px]',
      renderCell: ({ discount, tierIndex }) => (
        <div className="px-6">
          <DiscountCell {...{
            disabled: isEditDisabled,
            discount,
            tierIndex,
            lineItemId,
            productVariantId,
            currency,
          }}
          />
        </div>
      ),
    },
    {
      header: 'Discount',
      headerClassName: 'w-fit m-auto',
      cellClassName: 'justify-center flex',      
      renderCell: ({ discount, tierUnits, price }) => { 
        const calculatedPrice = isNotNullOrUndefined(discount)
          ? calculateDiscountPrice(tierUnits, price, discount)
          : undefined;

        const absoluteAmount = isNotNullOrUndefined(calculatedPrice)
          ? Math.abs(calculatedPrice)
          : undefined;

        const priceText = isNotNullOrUndefined(absoluteAmount)
          ? absoluteAmount
          : '--';

        return (
          <Price
            currency={currency}
          >
            {priceText}
          </Price>
        );
      },
    },
    {
      header: 'Amount',
      headerClassName: '',
      renderCell: ({ tierUnits, price, discount, flatRate }) => (
        <div className='m-auto w-fit'>
          <Price currency={currency}>
            {calculateAmountAfterDiscount(tierUnits, price, flatRate, discount)}
          </Price>
        </div>
      ),
    },
    {
      header: ' ',
      headerClassName: 'w-fit m-auto',
      renderCell: ({ tierIndex, discount }) => {
        return (
          <button
            disabled={isEditDisabled}
            className={classNames(
              'm-auto w-fit underline text-[#9EC3E4] opacity-0 pointer-events-none transition duration-200 ease-linear',
              { 'group-hover/row:opacity-100 pointer-events-auto': !!discount },
            )}
            onClick={() => onResetClick?.(lineItemId, productVariantId, tierIndex)}
          >
            Reset
          </button>
        );
      },
    },
  ]);
}

function calculateDiscountPrice(tierUnits: number, price: number, { type, amount }: TierDiscountConfiguration) {
  const currentPrice = tierUnits * price;

  switch (type) {
    case DISCOUNT_TYPES.Fixed:
      return -(tierUnits * (price - amount));
    case DISCOUNT_TYPES.Percentage:
      return -percentageRatio(currentPrice * (price - amount));
    default:
      return raiseUnreachable(type);
  }
}

function calculateAmountAfterDiscount(tierUnits: number, price: number, flatRate: number | undefined, discount: TierDiscountConfiguration | undefined) {
  const flatRateCharge = isNullOrUndefined(flatRate) || tierUnits <= 0 ? 0 : flatRate;
  const currentPrice = (tierUnits * price) + flatRateCharge;
  const discountPrice = isNullOrUndefined(discount) ? 0 : calculateDiscountPrice(tierUnits, price, discount);

  return currentPrice + discountPrice;
}